import * as React from 'react';
import styled, { css } from 'styled-components';
import { useForm, Controller } from 'react-hook-form';

import { TABLE_LOAD_DATA_MODE } from '../constants/settings';
import { ButtonMain, RadioButton } from '../atoms';

const OPTIONS = [
  {
    value: TABLE_LOAD_DATA_MODE.ALL,
    label: 'Load all',
    hint: 'Show every record, including deleted',
  },
  {
    value: TABLE_LOAD_DATA_MODE.ACTIVE,
    label: 'Operational',
    hint: 'Only active, non-deleted records',
  },
  {
    value: TABLE_LOAD_DATA_MODE.INACTIVE,
    label: 'Deleted/Inactive',
      hint: 'Only records marked as inactive or deleted',
  },
];

const TableSettingsForm = ({
  onSubmit,
  initialValues,
}: Props) => {
  const { control, handleSubmit } = useForm({
    defaultValues: initialValues,
  });

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Divider />
      <Label>Load all entries?</Label>
      <Controller
        name="tableLoadMode"
        control={control}
        render={({ value, onChange }) => (
          <OptionList>
            {OPTIONS.map(option => {
              const selected =
                String(value) === String(option.value);
              return (
                <OptionCard
                  key={option.value}
                  selected={selected}
                >
                  <RadioButton
                    checked={selected}
                    onChange={() => onChange(option.value)}
                  />
                  <OptionText>
                    <OptionLabel>{option.label}</OptionLabel>
                    <OptionHint>{option.hint}</OptionHint>
                  </OptionText>
                </OptionCard>
              );
            })}
          </OptionList>
        )}
      />
      <SubmitButton type="submit">Save settings</SubmitButton>
    </Form>
  );
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const Divider = styled.div`
  height: 1px;
  background: var(--neutral-200, #e5e7eb);
  margin: -10px -5px 20px;
`;

const Label = styled.div`
  color: var(--secondaryText);
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 12px;
`;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
`;

const OptionCard = styled.label<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid var(--neutral-200, #e5e7eb);
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: var(--primary-300, #93c5fd);
    background-color: var(--primary-50, #eff6ff);
  }

  ${({ selected }) =>
    selected &&
    css`
      border-color: var(--primary-600, #1d4ed8);
      background-color: var(--primary-50, #eff6ff);
    `}
`;

const OptionText = styled.div`
  display: flex;
  flex-direction: column;
`;

const OptionLabel = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--primaryText);
`;

const OptionHint = styled.span`
  font-size: 0.75rem;
  color: var(--secondaryText);
  margin-top: 2px;
`;

const SubmitButton = styled(ButtonMain)`
  && {
    width: 100%;
    height: auto;
  }
`;

interface Props {
  onSubmit: (data: any) => any;
  initialValues: object;
}

export default TableSettingsForm;
